import type { ReportFileFormat } from '../types/report';

export function sanitizeReportFileName(
  rawName: string | null | undefined,
  format: ReportFileFormat,
  reportCode?: string,
): string {
  const extension = getFileExtensionForFormat(format);
  const fallback = buildFallbackFileName(reportCode, extension);

  if (typeof rawName !== 'string' || !rawName.trim()) {
    return fallback;
  }

  let name = rawName
    .replace(/\0/g, '')
    // Strip ASCII control characters without a control-character regex class.
    .split('')
    .filter(char => {
      const code = char.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('')
    .replace(/\\/g, '/')
    .split('/')
    .pop()
    ?.trim() ?? '';

  name = name.replace(/\.\./g, '');
  name = name.replace(/[^A-Za-z0-9 _\-().]/g, '');
  name = name.replace(/\s+/g, ' ').trim();

  if (!name || name === '.' || name.length > 180) {
    return fallback;
  }

  const withoutExt = name.replace(/\.[^.]+$/i, '');
  const safeBase = withoutExt.replace(/[^A-Za-z0-9 _\-().]/g, '').trim();
  if (!safeBase) return fallback;

  return `${safeBase.slice(0, 160)}.${extension}`;
}

export function getFileExtensionForFormat(format: ReportFileFormat): string {
  if (format === 'PDF') return 'pdf';
  return format === 'CSV' ? 'csv' : 'xlsx';
}

export function isSupportedReportMimeType(
  mimeType: string | null | undefined,
  format: ReportFileFormat,
): boolean {
  if (typeof mimeType !== 'string' || !mimeType.trim()) return false;
  const value = mimeType.split(';')[0].trim().toLowerCase();

  if (format === 'PDF') {
    return value === 'application/pdf' || value === 'application/octet-stream';
  }

  if (format === 'CSV') {
    return (
      value === 'text/csv' ||
      value === 'application/csv' ||
      value === 'application/octet-stream'
    );
  }

  return (
    value ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    value === 'application/octet-stream'
  );
}

export function parseContentDispositionFileName(
  header: string | null | undefined,
): string | null {
  if (typeof header !== 'string' || !header.trim()) return null;

  const utfMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/"/g, ''));
    } catch {
      return utfMatch[1].trim().replace(/"/g, '');
    }
  }

  const plainMatch = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(
    header,
  );
  const raw = (plainMatch?.[1] || plainMatch?.[2] || '').trim();
  return raw || null;
}

function buildFallbackFileName(
  reportCode: string | undefined,
  extension: string,
): string {
  const now = new Date();
  const stamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    '-',
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
    String(now.getSeconds()).padStart(2, '0'),
  ].join('');

  const code =
    typeof reportCode === 'string' && reportCode.trim()
      ? reportCode
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
      : 'report';

  return `${code || 'report'}-${stamp}.${extension}`;
}
